// /admin/repartidores/:id — dashboard de performance de un repartidor.
// 4 ejes: volumen+éxito, tiempo+velocidad, geografía, motivos de fallo.
// Selector de período: 7 días / 30 días / vida.

import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { labelCortoVentana } from '../../lib/ventanasHorarias'
import { getEntregaFotoSignedUrl } from '../../lib/entregasApi'

const PERIODOS = [
  { v: '7d', label: '7 días', days: 7 },
  { v: '30d', label: '30 días', days: 30 },
  { v: 'vida', label: 'Vida', days: null },
]

const ESTADO_LABEL = {
  programada: '📅 Programada',
  en_ruta: '🚐 En ruta',
  entregada: '✅ Entregada',
  no_entregada: '❌ No entregada',
  cancelada: '🚫 Cancelada',
  pendiente_pago: '💳 Pendiente pago',
}
const ESTADO_COLOR = {
  programada: '#94a3b8',
  en_ruta: '#3b82f6',
  entregada: '#10b981',
  no_entregada: '#ef4444',
  cancelada: '#94a3b8',
  pendiente_pago: '#f59e0b',
}

// fecha_programada viene como 'YYYY-MM-DD' (fecha pura). new Date('YYYY-MM-DD') la interpreta como
// medianoche UTC y en UY (UTC-3) se ve un día antes (bug "viaje del 5 cuando fue el 6").
// Parseamos los YYYY-MM-DD como fecha LOCAL; timestamps completos van como vienen.
const fmtFecha = (d) => {
  if (!d) return '—'
  const s = String(d)
  const mm = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const dt = mm ? new Date(+mm[1], +mm[2] - 1, +mm[3]) : new Date(s)
  return dt.toLocaleDateString('es-UY', { day: '2-digit', month: 'short' })
}
const fmtDateTime = (d) => d
  ? new Date(d).toLocaleString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '—'

function fmtMinutos(m) {
  if (m == null) return '—'
  if (m < 60) return `${Math.round(m)} min`
  const h = Math.floor(m / 60)
  const mm = Math.round(m % 60)
  return `${h}h ${mm}m`
}

// Fecha local YYYY-MM-DD. NO usar toISOString (da UTC y en UY corre el día en la tarde/noche).
function localISO(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
function hoyISO() {
  return localISO(new Date())
}
function ayerISO() {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return localISO(d)
}

export default function RepartidorDetalle() {
  const { id } = useParams()
  const [periodo, setPeriodo] = useState('30d')
  const [diaEspecifico, setDiaEspecifico] = useState('') // YYYY-MM-DD, prioridad sobre periodo
  const [rep, setRep] = useState(null)
  const [profile, setProfile] = useState(null)
  const [entregas, setEntregas] = useState([])
  const [ventasML, setVentasML] = useState({}) // entrega_id -> { neto_real, neto, monto_bruto }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editando, setEditando] = useState(false)
  const [form, setForm] = useState({})
  const [guardando, setGuardando] = useState(false)
  const [guardarMsg, setGuardarMsg] = useState(null)

  useEffect(() => { cargar() }, [id])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const [{ data: r, error: re }, { data: p, error: pe }, { data: es, error: ee }] = await Promise.all([
        supabase.from('repartidores')
          .select('id, vehiculo, zona, notas, activo, created_at, origen_lat, origen_lng, origen_descripcion, combustible_tipo, km_por_litro_override, precio_litro_override, tarifa_facturada_por_viaje, regresa_a_origen')
          .eq('id', id).maybeSingle(),
        supabase.from('profiles')
          .select('id, nombre, email, phone, created_at')
          .eq('id', id).maybeSingle(),
        supabase.from('entregas')
          .select('id, estado, fecha_programada, ventana_horaria, bultos_total, ts_en_ruta, ts_entregada, ts_no_entregada, motivo_no_entrega, barrio_snapshot, monto_total, numero_orden, cliente_id, foto_url, tracking_token, km_recorridos, km_regreso_origen, km_origen, canal')
          .eq('repartidor_id', id)
          .order('fecha_programada', { ascending: false })
          .limit(1000),
      ])
      if (re) throw re
      if (pe) throw pe
      if (ee) throw ee
      setRep(r)
      setProfile(p)
      setEntregas(es || [])

      // Lo que pagó ML por cada entrega ML (neto_real = neto de comisión + crédito Flex).
      const entregaIds = (es || []).map(x => x.id)
      const vMap = {}
      if (entregaIds.length > 0) {
        const { data: vs, error: ve } = await supabase
          .from('ml_ventas')
          .select('entrega_id, neto_real, neto, monto_bruto')
          .in('entrega_id', entregaIds)
        if (ve) throw ve
        ;(vs || []).forEach(v => { if (v.entrega_id) vMap[v.entrega_id] = v })
      }
      setVentasML(vMap)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  function abrirEdicion() {
    setGuardarMsg(null)
    setForm({
      nombre: profile?.nombre || '',
      phone: profile?.phone || '',
      vehiculo: rep?.vehiculo || '',
      zona: rep?.zona || '',
      activo: !!rep?.activo,
      notas: rep?.notas || '',
      origen_descripcion: rep?.origen_descripcion || '',
      origen_lat: rep?.origen_lat ?? '',
      origen_lng: rep?.origen_lng ?? '',
      combustible_tipo: rep?.combustible_tipo || '',
      km_por_litro_override: rep?.km_por_litro_override ?? '',
      precio_litro_override: rep?.precio_litro_override ?? '',
      tarifa_facturada_por_viaje: rep?.tarifa_facturada_por_viaje ?? '',
    })
    setEditando(true)
  }

  function setF(k, v) { setForm(prev => ({ ...prev, [k]: v })) }
  const numOrNull = (v) => (v === '' || v == null ? null : Number(v))

  async function guardarDatos() {
    setGuardando(true)
    setGuardarMsg(null)
    try {
      // profiles: admin/superadmin puede actualizar. repartidores: solo superadmin (RLS).
      const { error: ep } = await supabase.from('profiles')
        .update({ nombre: form.nombre?.trim() || null, phone: form.phone?.trim() || null })
        .eq('id', id)
      if (ep) throw ep
      const { error: er } = await supabase.from('repartidores')
        .update({
          vehiculo: form.vehiculo?.trim() || null,
          zona: form.zona?.trim() || null,
          activo: !!form.activo,
          notas: form.notas?.trim() || null,
          origen_descripcion: form.origen_descripcion?.trim() || null,
          origen_lat: numOrNull(form.origen_lat),
          origen_lng: numOrNull(form.origen_lng),
          combustible_tipo: form.combustible_tipo?.trim() || null,
          km_por_litro_override: numOrNull(form.km_por_litro_override),
          precio_litro_override: numOrNull(form.precio_litro_override),
          tarifa_facturada_por_viaje: numOrNull(form.tarifa_facturada_por_viaje),
        })
        .eq('id', id)
      if (er) throw er
      await cargar()
      setEditando(false)
    } catch (e) {
      setGuardarMsg(e.message || String(e))
    } finally {
      setGuardando(false)
    }
  }

  // Filtrado: día específico tiene prioridad sobre período
  const entregasFiltradas = useMemo(() => {
    if (diaEspecifico) {
      return entregas.filter(e => e.fecha_programada === diaEspecifico)
    }
    if (periodo === 'vida') return entregas
    const days = PERIODOS.find(p => p.v === periodo).days
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - days)
    const cutoffISO = localISO(cutoff)
    return entregas.filter(e => (e.fecha_programada || '') >= cutoffISO)
  }, [entregas, periodo, diaEspecifico])

  // Métricas
  const m = useMemo(() => {
    const es = entregasFiltradas
    const total = es.length
    const entregadas = es.filter(e => e.estado === 'entregada').length
    const noEntregadas = es.filter(e => e.estado === 'no_entregada').length
    const enRuta = es.filter(e => e.estado === 'en_ruta').length
    const programadas = es.filter(e => e.estado === 'programada').length
    const tasaExito = (entregadas + noEntregadas) > 0
      ? (entregadas / (entregadas + noEntregadas)) * 100
      : null
    const bultos = es.reduce((acc, e) => acc + (e.bultos_total || 0), 0)
    // Km del período: entregas (hops) + regresos al origen (última del día).
    // Regla Nico 06-ago: cada día se paga origen→e1→e2→…→origen (ida y vuelta completas).
    const kmEntregas = es.reduce((acc, e) => acc + (Number(e.km_recorridos) || 0), 0)
    const kmRegreso  = es.reduce((acc, e) => acc + (Number(e.km_regreso_origen) || 0), 0)
    const kmTotal    = kmEntregas + kmRegreso
    // Split por canal
    const sumaKmCanal = (canal) => es
      .filter(e => (e.canal || 'limpack') === canal)
      .reduce((acc, e) => acc + (Number(e.km_recorridos) || 0) + (Number(e.km_regreso_origen) || 0), 0)
    const kmTienda = sumaKmCanal('limpack')
    const kmML     = sumaKmCanal('mercadolibre')
    // Costo combustible estimado: usa data del REPARTIDOR con fallback a globales.
    // Match con lo que calculan las vistas SQL v_repartidor_km_*.
    const PRECIOS_GLOBALES = { nafta: 88.67, gasoil: 58.68 }
    const KMPL_DEFAULT = 10
    const tipoCombustible     = rep?.combustible_tipo || 'nafta'
    const precioLitroEfectivo = Number(rep?.precio_litro_override) || PRECIOS_GLOBALES[tipoCombustible] || 0
    const kmPorLitroEfectivo  = Number(rep?.km_por_litro_override) || KMPL_DEFAULT
    const tarifaPorViaje      = Number(rep?.tarifa_facturada_por_viaje) || 0
    const costoCombustibleEst = kmPorLitroEfectivo > 0 ? kmTotal * precioLitroEfectivo / kmPorLitroEfectivo : 0
    const costoFacturadoTercero = entregadas * tarifaPorViaje

    // Compat: mantener nombre viejo por si algún componente lo usa
    const costoNaftaEst = costoCombustibleEst

    // Promedios útiles (evitar div/0)
    const kmPromEntrega    = entregadas > 0 ? kmTotal / entregadas : 0
    const bultosPromEntrega = entregadas > 0 ? bultos  / entregadas : 0
    const costoPromEntrega = entregadas > 0 ? costoCombustibleEst / entregadas : 0

    // Tiempo promedio en ruta (de ts_en_ruta a ts_entregada/no_entregada)
    const tiempos = es
      .filter(e => e.ts_en_ruta && (e.ts_entregada || e.ts_no_entregada))
      .map(e => {
        const fin = new Date(e.ts_entregada || e.ts_no_entregada)
        const ini = new Date(e.ts_en_ruta)
        return (fin - ini) / 60000 // minutos
      })
    const tiempoProm = tiempos.length > 0
      ? tiempos.reduce((a, b) => a + b, 0) / tiempos.length
      : null

    // Top barrios: los ML (sin barrio_snapshot) se cuentan como una categoría aparte
    // para no confundir "(sin barrio)" con un vacío misterioso.
    const barriosMap = new Map()
    let mlSinBarrio = 0
    es.forEach(e => {
      if ((e.canal || 'limpack') === 'mercadolibre' && !e.barrio_snapshot) {
        mlSinBarrio++
        return
      }
      const b = e.barrio_snapshot || '(sin barrio)'
      barriosMap.set(b, (barriosMap.get(b) || 0) + 1)
    })
    if (mlSinBarrio > 0) {
      barriosMap.set('🛒 ML (varios)', mlSinBarrio)
    }
    const topBarrios = [...barriosMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)

    // Top motivos no_entregada
    const motivosMap = new Map()
    es.filter(e => e.estado === 'no_entregada').forEach(e => {
      const k = (e.motivo_no_entrega || '').split('—')[0].trim() || '(sin motivo)'
      motivosMap.set(k, (motivosMap.get(k) || 0) + 1)
    })
    const topMotivos = [...motivosMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)

    // Por día (gráfico + tabla km/día). km = hop + regreso al origen.
    const diasMap = new Map()
    es.forEach(e => {
      const d = e.fecha_programada
      if (!d) return
      const c = diasMap.get(d) || { entregadas: 0, no_entregadas: 0, otras: 0, km: 0, km_regreso: 0 }
      if (e.estado === 'entregada') c.entregadas++
      else if (e.estado === 'no_entregada') c.no_entregadas++
      else c.otras++
      c.km         += Number(e.km_recorridos)     || 0
      c.km_regreso += Number(e.km_regreso_origen) || 0
      diasMap.set(d, c)
    })
    const porDia = [...diasMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14) // últimos 14 días con data

    // Km por día (ordenado más reciente primero, para la tabla)
    const kmPorDia = [...diasMap.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([fecha, c]) => {
        const kmDia = c.km + c.km_regreso
        return {
          fecha,
          entregas: c.entregadas + c.no_entregadas + c.otras,
          entregadas: c.entregadas,
          km_hop: c.km,
          km_regreso: c.km_regreso,
          km: kmDia,
          costo_combustible: kmPorLitroEfectivo > 0 ? kmDia * precioLitroEfectivo / kmPorLitroEfectivo : 0,
        }
      })
      .filter(d => d.entregadas > 0 || d.km > 0)

    return {
      total, entregadas, noEntregadas, enRuta, programadas,
      tasaExito, bultos, tiempoProm, kmTotal, kmEntregas, kmRegreso,
      costoNaftaEst, costoCombustibleEst, costoFacturadoTercero,
      tipoCombustible, precioLitroEfectivo, kmPorLitroEfectivo, tarifaPorViaje,
      kmPromEntrega, bultosPromEntrega, costoPromEntrega,
      kmTienda, kmML,
      topBarrios, topMotivos, porDia, kmPorDia,
    }
  }, [entregasFiltradas, rep])

  if (loading) {
    return <AdminLayout><div className="page"><div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Cargando…</div></div></AdminLayout>
  }
  if (error) {
    return <AdminLayout><div className="page"><div className="alert-error">{error}</div></div></AdminLayout>
  }
  if (!rep || !profile) {
    return (
      <AdminLayout>
        <div className="page">
          <Link to="/admin/repartidores" style={{ color: '#1a3461' }}>← Volver</Link>
          <h1>Repartidor no encontrado</h1>
        </div>
      </AdminLayout>
    )
  }

  const nombreCompleto = profile.nombre || profile.email

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/repartidores" style={{ color: '#1a3461', fontSize: 14, textDecoration: 'none' }}>← Repartidores</Link>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: rep.activo ? '#1a3461' : '#94a3b8',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 700, flexShrink: 0,
          }}>
            {(nombreCompleto || '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, color: '#1a3461', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {nombreCompleto}
              {!rep.activo && <span className="pill pill-grey">Inactivo</span>}
              {rep.activo && <span className="pill pill-green">Activo</span>}
            </h1>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
              {profile.email && <span>📧 {profile.email}</span>}
              {profile.phone && <span style={{ marginLeft: 12 }}>📱 {profile.phone}</span>}
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
              {rep.vehiculo && <span>🚐 {rep.vehiculo}</span>}
              {rep.zona && <span style={{ marginLeft: 12 }}>🗺️ {rep.zona}</span>}
            </div>
          </div>
          {!editando && (
            <button onClick={abrirEdicion} className="ap-filtro" style={{ flexShrink: 0 }}>
              ✏️ Editar datos
            </button>
          )}
        </div>

        {editando && (
          <EditorRepartidor
            form={form}
            setF={setF}
            guardando={guardando}
            msg={guardarMsg}
            onGuardar={guardarDatos}
            onCancelar={() => setEditando(false)}
          />
        )}

        {/* Filtros: período + atajos + día específico */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
          marginBottom: 16, padding: 12, background: '#fff',
          border: '1px solid #e2e8f0', borderRadius: 12,
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Filtrar:
          </span>

          {/* Atajos rápidos: Hoy / Ayer */}
          <button
            className={`ap-filtro ${diaEspecifico === hoyISO() ? 'is-active' : ''}`}
            onClick={() => setDiaEspecifico(hoyISO())}
          >
            📅 Hoy
          </button>
          <button
            className={`ap-filtro ${diaEspecifico === ayerISO() ? 'is-active' : ''}`}
            onClick={() => setDiaEspecifico(ayerISO())}
          >
            Ayer
          </button>

          {/* Chips de período (se deshabilitan visualmente si hay día específico) */}
          {PERIODOS.map(p => (
            <button key={p.v}
              className={`ap-filtro ${!diaEspecifico && periodo === p.v ? 'is-active' : ''}`}
              onClick={() => { setDiaEspecifico(''); setPeriodo(p.v) }}
              style={diaEspecifico ? { opacity: .5 } : null}
            >
              {p.label}
            </button>
          ))}

          {/* Input de fecha específica */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            <input
              type="date"
              value={diaEspecifico}
              onChange={(e) => setDiaEspecifico(e.target.value)}
              style={{
                padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8,
                fontSize: 13, color: '#1a3461',
              }}
            />
            {diaEspecifico && (
              <button
                onClick={() => setDiaEspecifico('')}
                style={{
                  padding: '6px 10px', background: '#fee2e2', color: '#991b1b',
                  border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontSize: 12, fontWeight: 700,
                }}
                title="Volver a ver por período"
              >
                ✕ Quitar fecha
              </button>
            )}
          </div>
        </div>

        {/* Cards principales */}
        <div className="cards-grid" style={{ marginBottom: 20 }}>
          <div className="card">
            <h3>Entregas</h3>
            <div className="stat">
              <span style={{ color: '#10b981' }}>{m.entregadas}</span>
              <span style={{ fontSize: 16, color: '#94a3b8', fontWeight: 500 }}> / {m.total}</span>
            </div>
            <div className="sub">
              <span style={{ color: '#10b981', fontWeight: 700 }}>✓ {m.entregadas} entregadas</span>
              {m.noEntregadas > 0 && <div style={{ color: '#ef4444' }}>✗ {m.noEntregadas} fallidas</div>}
              {(m.enRuta + m.programadas) > 0 && (
                <div style={{ color: '#94a3b8' }}>⏳ {m.enRuta + m.programadas} pendientes</div>
              )}
            </div>
          </div>
          <div className="card">
            <h3>Tasa de éxito</h3>
            <div className="stat" style={{ color: m.tasaExito == null ? '#94a3b8' : m.tasaExito >= 95 ? '#10b981' : m.tasaExito >= 85 ? '#f59e0b' : '#ef4444' }}>
              {m.tasaExito == null ? '—' : `${m.tasaExito.toFixed(0)}%`}
            </div>
            <div className="sub">
              {m.tasaExito == null
                ? 'sin entregas completadas todavía'
                : <>{m.entregadas + m.noEntregadas > 0 ? `${m.entregadas} de ${m.entregadas + m.noEntregadas} cerradas` : '—'}{m.noEntregadas > 0 && ` · ${m.noEntregadas} fallidas`}</>}
            </div>
          </div>
          <div className="card">
            <h3>Tiempo promedio en ruta</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>{fmtMinutos(m.tiempoProm)}</div>
            <div className="sub">de "en ruta" a cerrar · {m.entregadas > 0 ? `n=${m.entregadas}` : 'sin data'}</div>
          </div>
          <div className="card">
            <h3>Bultos repartidos</h3>
            <div className="stat">{m.bultos}</div>
            <div className="sub">
              {m.entregadas > 0
                ? <>{m.bultosPromEntrega.toFixed(1)} por entrega · {m.entregadas} entregadas</>
                : 'total en el período'}
            </div>
          </div>
          <div className="card">
            <h3>Km recorridos</h3>
            <div className="stat" style={{ color: '#1a3461' }}>
              {m.kmTotal > 0 ? `${m.kmTotal.toFixed(1)}` : '—'}
              {m.kmTotal > 0 && <span style={{ fontSize: 16, marginLeft: 6, color: '#94a3b8' }}>km</span>}
            </div>
            <div className="sub">
              {m.kmTotal > 0
                ? (
                  <>
                    🏪 <strong>{m.kmTienda.toFixed(1)}</strong> tienda · 🛒 <strong style={{ color: m.kmML > 0 ? '#3730A3' : '#94a3b8' }}>{m.kmML.toFixed(1)}</strong> ML
                    <div>{m.kmEntregas.toFixed(1)} entregas + {m.kmRegreso.toFixed(1)} regreso</div>
                    <div>≈ <strong>${Math.round(m.costoCombustibleEst).toLocaleString('es-UY')}</strong> de {m.tipoCombustible === 'gasoil' ? 'gasoil' : 'nafta'}
                      <span style={{ color: '#cbd5e1' }}> · ${m.precioLitroEfectivo}/L · {m.kmPorLitroEfectivo} km/L</span></div>
                    {m.entregadas > 0 && <div style={{ color: '#94a3b8' }}>{m.kmPromEntrega.toFixed(1)} km / entrega · ${Math.round(m.costoPromEntrega)} / entrega</div>}
                    {m.costoFacturadoTercero > 0 && (
                      <div style={{ color: '#b45309', marginTop: 3 }}>
                        + ${Math.round(m.costoFacturadoTercero).toLocaleString('es-UY')} facturado ({m.entregadas} × ${m.tarifaPorViaje})
                      </div>
                    )}
                  </>
                )
                : 'se calcula al pasar a entregada'}
            </div>
          </div>
        </div>

        {/* Integridad de datos: entregas entregadas sin lat/lng (no cuentan km) */}
        {(() => {
          const entregadasSinCoord = entregasFiltradas.filter(e =>
            e.estado === 'entregada' && (!e.km_recorridos || Number(e.km_recorridos) === 0) && !e.km_regreso_origen
          )
          if (entregadasSinCoord.length === 0) return null
          return (
            <div style={{
              padding: '12px 16px', background: '#FEF3C7', border: '1px solid #FDE68A',
              borderRadius: 10, marginBottom: 20, fontSize: 13, color: '#78350f',
            }}>
              <strong>⚠ {entregadasSinCoord.length} {entregadasSinCoord.length === 1 ? 'entrega entregada' : 'entregas entregadas'} sin km calculado</strong>
              <div style={{ marginTop: 4, fontSize: 12 }}>
                Probablemente falta lat/lng en la dirección. Los km reales no están sumados al total.
                {' '}Órdenes: {entregadasSinCoord.slice(0, 4).map(e => e.numero_orden).join(', ')}
                {entregadasSinCoord.length > 4 && ` (+${entregadasSinCoord.length - 4})`}
              </div>
            </div>
          )
        })()}

        {/* Nota si NO regresa al origen (tercerizados como Nahuel) */}
        {rep?.regresa_a_origen === false && (
          <div style={{
            padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: 10, marginBottom: 20, fontSize: 12.5, color: '#1E40AF',
          }}>
            ℹ Este repartidor no vuelve al depósito al final del día (tercerizado). Los km calculan solo origen → e1 → e2 → … → última, sin regreso.
          </div>
        )}

        {/* Gráfico simple por día */}
        {m.porDia.length > 0 && (
          <section style={{ marginBottom: 24 }}>
            <h2>📈 Entregas por día (últimos 14 con actividad)</h2>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, padding: '16px 0', borderBottom: '1px solid #e5e7eb' }}>
              {m.porDia.map(([fecha, c]) => {
                const max = Math.max(...m.porDia.map(d => d[1].entregadas + d[1].no_entregadas + d[1].otras))
                const tot = c.entregadas + c.no_entregadas + c.otras
                const h = max > 0 ? Math.max(15, (tot / max) * 100) : 0
                return (
                  <div key={fecha} style={{ flex: 1, textAlign: 'center', minWidth: 32 }}>
                    <div style={{
                      height: `${h}%`,
                      background: 'linear-gradient(to top, #1a3461, #00a8b5)',
                      borderRadius: '4px 4px 0 0',
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                      color: '#fff', fontSize: 11, fontWeight: 700, paddingTop: 4,
                    }}>
                      {tot}
                    </div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>{fmtFecha(fecha)}</div>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* Km por día (tabla) */}
        {m.kmPorDia.length > 0 && (
          <section style={{ marginBottom: 24 }}>
            <h2>🚗 Km por día</h2>
            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <table className="rep-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th style={{ textAlign: 'center' }}>Entregas</th>
                    <th style={{ textAlign: 'center' }}>Entregadas</th>
                    <th style={{ textAlign: 'right' }}>Km entregas</th>
                    <th style={{ textAlign: 'right' }}>+ regreso</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'right' }}>Nafta est.</th>
                  </tr>
                </thead>
                <tbody>
                  {m.kmPorDia.map(d => (
                    <tr key={d.fecha}
                        className="row-link"
                        onClick={() => setDiaEspecifico(d.fecha)}
                        title="Filtrar por este día">
                      <td style={{ fontWeight: 700, color: '#1a3461' }}>{fmtFecha(d.fecha)}</td>
                      <td style={{ textAlign: 'center' }}>{d.entregas}</td>
                      <td style={{ textAlign: 'center', color: '#10b981', fontWeight: 700 }}>{d.entregadas}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {d.km_hop > 0 ? `${d.km_hop.toFixed(1)}` : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>
                        {d.km_regreso > 0 ? `${d.km_regreso.toFixed(1)}` : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: '#1a3461' }}>
                        {d.km > 0 ? `${d.km.toFixed(1)} km` : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: '#6b7280' }}>
                        {d.costo_combustible > 0 ? `$${Math.round(d.costo_combustible).toLocaleString('es-UY')}` : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f8fafc', fontWeight: 800 }}>
                    <td>Total período</td>
                    <td style={{ textAlign: 'center' }}>{m.total}</td>
                    <td style={{ textAlign: 'center', color: '#10b981' }}>{m.entregadas}</td>
                    <td style={{ textAlign: 'right' }}>{m.kmEntregas.toFixed(1)}</td>
                    <td style={{ textAlign: 'right', color: '#64748b' }}>{m.kmRegreso.toFixed(1)}</td>
                    <td style={{ textAlign: 'right', color: '#1a3461' }}>{m.kmTotal.toFixed(1)} km</td>
                    <td style={{ textAlign: 'right', color: '#1a3461' }}>${Math.round(m.costoNaftaEst).toLocaleString('es-UY')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              Km = origen → cada entrega + regreso al origen desde la última.
              Ejemplo 3 entregas: origen→e1 + e1→e2 + e2→e3 + e3→origen.
              Fórmula: haversine × 1.3 (aprox ruta por calles Mvd). Editá el origen del repartidor si arranca de un lugar distinto al depósito.
            </div>
          </section>
        )}

        {/* 2 columnas: top barrios + motivos de fallo */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
          <section className="panel">
            <h3>🗺️ Top barrios</h3>
            {m.topBarrios.length === 0 ? (
              <div className="empty-mini">Sin datos en el período</div>
            ) : (
              <ul className="rank-list">
                {m.topBarrios.map(([b, c]) => (
                  <li key={b}>
                    <span>{b}</span>
                    <strong>{c}</strong>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="panel">
            <h3>❌ Top motivos de no entrega</h3>
            {m.topMotivos.length === 0 ? (
              <div className="empty-mini" style={{ color: '#10b981' }}>0 fallidas en el período 🎉</div>
            ) : (
              <ul className="rank-list">
                {m.topMotivos.map(([motivo, c]) => (
                  <li key={motivo}>
                    <span>{motivo}</span>
                    <strong>{c}</strong>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* Tabla de entregas recientes */}
        <section>
          <h2>📦 Entregas recientes ({entregasFiltradas.length})</h2>
          {entregasFiltradas.length === 0 ? (
            <div className="ap-empty">
              <div style={{ fontSize: 48 }}>📭</div>
              <h3>Sin entregas en el período</h3>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <table className="rep-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center' }}>Foto</th>
                    <th>Orden</th>
                    <th>Canal</th>
                    <th>Fecha</th>
                    <th>Barrio</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'center' }}>Bultos</th>
                    <th style={{ textAlign: 'right' }}>Km</th>
                    <th>En ruta → cerrada</th>
                    <th style={{ textAlign: 'right' }}>Valor entrega</th>
                    <th style={{ textAlign: 'right' }}>Pagó ML</th>
                    <th style={{ textAlign: 'right' }} title={m.tarifaPorViaje > 0 ? 'Lo que te cobra el repartidor por viaje' : 'Nafta estimada del reparto'}>
                      {m.tarifaPorViaje > 0 ? 'Nos cobró' : 'Nafta est.'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entregasFiltradas.slice(0, 50).map(e => {
                    const dur = e.ts_en_ruta && (e.ts_entregada || e.ts_no_entregada)
                      ? (new Date(e.ts_entregada || e.ts_no_entregada) - new Date(e.ts_en_ruta)) / 60000
                      : null
                    // Lo que ML nos depositó por esta entrega (neto de comisión + crédito Flex).
                    const pagoML = e.canal === 'mercadolibre'
                      ? (ventasML[e.id]?.neto_real ?? ventasML[e.id]?.neto ?? null)
                      : null
                    // Costo del reparto de esta entrega:
                    //  - Tercerizado con tarifa (Nahuel): lo que te cobra por viaje.
                    //  - Repartidor propio (Martín/vos): nafta estimada de esa entrega.
                    const kmRow = (Number(e.km_recorridos) || 0) + (Number(e.km_regreso_origen) || 0)
                    const costoReparto = m.tarifaPorViaje > 0
                      ? (e.estado === 'entregada' ? m.tarifaPorViaje : null)
                      : (m.kmPorLitroEfectivo > 0 && kmRow > 0 ? kmRow * m.precioLitroEfectivo / m.kmPorLitroEfectivo : null)
                    return (
                      <tr key={e.id}>
                        <td style={{ textAlign: 'center' }}>
                          <FotoThumb path={e.foto_url} />
                        </td>
                        <td>
                          <Link
                            to={`/d/${e.tracking_token}`}
                            style={{
                              fontFamily: 'monospace', fontSize: 12,
                              color: '#1a3461', fontWeight: 700,
                              textDecoration: 'none',
                            }}
                            title="Ver detalle de la entrega"
                          >
                            {e.numero_orden} →
                          </Link>
                        </td>
                        <td>
                          {e.canal === 'mercadolibre'
                            ? <span style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: '#FFF4CC', color: '#7C5C00', fontWeight: 700 }}>🛒 ML</span>
                            : <span style={{ fontSize: 10.5, padding: '2px 7px', borderRadius: 999, background: '#DBEAFE', color: '#1E40AF', fontWeight: 700 }}>🏪 Tienda</span>}
                        </td>
                        <td style={{ fontSize: 12 }}>{fmtFecha(e.fecha_programada)} <span style={{ color: '#94a3b8' }}>· {labelCortoVentana(e.ventana_horaria)}</span></td>
                        <td style={{ fontSize: 13, color: '#475569' }}>{e.barrio_snapshot || '—'}</td>
                        <td>
                          <span className="pill" style={{ background: ESTADO_COLOR[e.estado] + '22', color: ESTADO_COLOR[e.estado] }}>
                            {ESTADO_LABEL[e.estado] || e.estado}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', fontSize: 13 }}>{e.bultos_total}</td>
                        <td style={{ textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                          {e.km_recorridos != null ? (
                            <span title={`Hop: ${Number(e.km_recorridos).toFixed(2)} km · Origen: ${e.km_origen || '—'}${e.km_regreso_origen != null ? ` · +${Number(e.km_regreso_origen).toFixed(2)} regreso al origen (última del día)` : ''}`}>
                              {Number(e.km_recorridos).toFixed(1)}
                              {e.km_regreso_origen != null && (
                                <span style={{ color: '#64748b' }}> +{Number(e.km_regreso_origen).toFixed(1)}</span>
                              )}
                            </span>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: '#6b7280' }}>{fmtMinutos(dur)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                          {e.monto_total > 0 ? `$${Number(e.monto_total).toLocaleString('es-UY')}` : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: pagoML != null ? '#166534' : '#cbd5e1' }}
                            title={e.canal === 'mercadolibre' ? 'Neto depositado por ML (comisión ML descontada + crédito de envío Flex)' : 'Venta propia — no aplica'}>
                          {pagoML != null ? `$${Number(pagoML).toLocaleString('es-UY')}` : '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: costoReparto != null ? (m.tarifaPorViaje > 0 ? '#b45309' : '#475569') : '#cbd5e1' }}
                            title={m.tarifaPorViaje > 0 ? 'Tarifa que cobra el repartidor por viaje' : 'Nafta estimada de esta entrega (km × precio litro ÷ km/l)'}>
                          {costoReparto != null ? `$${Math.round(costoReparto).toLocaleString('es-UY')}` : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  {(() => {
                    const totValor = entregasFiltradas.reduce((s, e) => s + (Number(e.monto_total) || 0), 0)
                    const totML = entregasFiltradas.reduce((s, e) => s + (e.canal === 'mercadolibre' ? Number(ventasML[e.id]?.neto_real ?? ventasML[e.id]?.neto ?? 0) : 0), 0)
                    // Costo reparto total: tarifa × entregas (tercerizado) o nafta estimada del período (propio).
                    const totCobro = m.tarifaPorViaje > 0 ? (m.costoFacturadoTercero || 0) : (m.costoCombustibleEst || 0)
                    const fmt = (n) => `$${Math.round(n).toLocaleString('es-UY')}`
                    return (
                      <tr style={{ fontWeight: 800, background: '#f8fafc', borderTop: '2px solid #e5e7eb' }}>
                        <td colSpan={9} style={{ textAlign: 'right', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.03em' }}>Totales del período</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#1a3461' }}>{fmt(totValor)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#166534' }}>{totML > 0 ? fmt(totML) : '—'}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: m.tarifaPorViaje > 0 ? '#b45309' : '#475569' }}>{totCobro > 0 ? fmt(totCobro) : '—'}</td>
                      </tr>
                    )
                  })()}
                </tfoot>
              </table>
              {entregasFiltradas.length > 50 && (
                <div style={{ padding: 12, textAlign: 'center', fontSize: 12, color: '#6b7280', borderTop: '1px solid #f1f5f9' }}>
                  Mostrando 50 de {entregasFiltradas.length} · ajustá el período para ver más
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <style>{`
        .pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; }
        .pill-green { background: #dcfce7; color: #166534; }
        .pill-grey { background: #f1f5f9; color: #64748b; }
        .stat { font-size: 36px; font-weight: 800; color: #1a3461; margin: 4px 0; line-height: 1; }
        .sub { font-size: 12px; color: #6b7280; }
        .panel {
          background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
          padding: 16px;
        }
        .panel h3 { margin: 0 0 12px; color: #1a3461; font-size: 14px; }
        .rank-list { list-style: none; padding: 0; margin: 0; }
        .rank-list li {
          display: flex; justify-content: space-between; padding: 8px 0;
          border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #475569;
        }
        .rank-list li:last-child { border-bottom: none; }
        .rank-list li strong { color: #1a3461; font-variant-numeric: tabular-nums; }
        .empty-mini { padding: 16px; text-align: center; color: #94a3b8; font-size: 13px; }
        .alert-error {
          padding: 12px; background: #fee2e2; border: 1px solid #fca5a5;
          border-radius: 8px; color: #991b1b;
        }
        .ap-filtros { display: flex; gap: 6px; flex-wrap: wrap; }
        .ap-filtro {
          padding: 8px 14px; border: 1px solid #e5e7eb; background: #fff;
          border-radius: 999px; cursor: pointer; font-size: 13px; font-weight: 600; color: #475569;
        }
        .ap-filtro.is-active { background: #1a3461; color: #fff; border-color: #1a3461; }
        .ap-filtro:hover:not(.is-active) { background: #f1f5f9; }
        .rep-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .rep-table thead th {
          text-align: left; padding: 10px 12px; background: #f8fafc;
          border-bottom: 1px solid #e5e7eb; font-size: 11px;
          text-transform: uppercase; color: #6b7280; font-weight: 700;
        }
        .rep-table tbody td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        .rep-table tbody tr:hover { background: #f8fafc; }
        .ap-empty { padding: 40px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}

/* ───────── Thumbnail de foto desde bucket privado (signed URL) ─────────
   Genera la URL solo al montar. Si no hay path, muestra placeholder discreto. */
function FotoThumb({ path }) {
  const [url, setUrl] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!path) return
    let cancel = false
    setLoading(true)
    getEntregaFotoSignedUrl(path).then(u => {
      if (!cancel) {
        setUrl(u)
        setLoading(false)
      }
    })
    return () => { cancel = true }
  }, [path])

  if (!path) {
    return <span style={{ color: '#cbd5e1', fontSize: 16 }}>—</span>
  }
  if (loading || !url) {
    return <span style={{ color: '#94a3b8', fontSize: 11 }}>···</span>
  }
  return (
    <a href={url} target="_blank" rel="noopener" title="Ver foto grande">
      <img
        src={url}
        alt="prueba de entrega"
        style={{
          width: 44, height: 44, objectFit: 'cover',
          borderRadius: 6, border: '1px solid #e2e8f0',
          display: 'block', cursor: 'pointer',
        }}
      />
    </a>
  )
}

/* ───────── Editor de datos del repartidor ─────────
   Campo definido a nivel módulo (estable) para no perder foco al tipear. */
const EDIT_INPUT = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, color: '#1a3461', width: '100%', boxSizing: 'border-box' }
const EDIT_LABEL = { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', marginBottom: 4 }

function Campo({ label, children }) {
  return <div><label style={EDIT_LABEL}>{label}</label>{children}</div>
}

function EditorRepartidor({ form, setF, guardando, msg, onGuardar, onCancelar }) {
  return (
    <div style={{ marginBottom: 16, padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
      <h3 style={{ margin: '0 0 12px', color: '#1a3461' }}>✏️ Editar datos del repartidor</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Campo label="Nombre"><input style={EDIT_INPUT} value={form.nombre} onChange={e => setF('nombre', e.target.value)} /></Campo>
        <Campo label="Teléfono"><input style={EDIT_INPUT} value={form.phone} onChange={e => setF('phone', e.target.value)} /></Campo>
        <Campo label="Vehículo"><input style={EDIT_INPUT} value={form.vehiculo} onChange={e => setF('vehiculo', e.target.value)} placeholder="Ej: Fiat Fiorino" /></Campo>
        <Campo label="Zona"><input style={EDIT_INPUT} value={form.zona} onChange={e => setF('zona', e.target.value)} /></Campo>
        <Campo label="Combustible"><input style={EDIT_INPUT} value={form.combustible_tipo} onChange={e => setF('combustible_tipo', e.target.value)} placeholder="nafta / gasoil" /></Campo>
        <Campo label="Origen (descripción)"><input style={EDIT_INPUT} value={form.origen_descripcion} onChange={e => setF('origen_descripcion', e.target.value)} placeholder="Depósito / dirección de arranque" /></Campo>
        <Campo label="Origen lat"><input style={EDIT_INPUT} type="number" step="any" value={form.origen_lat} onChange={e => setF('origen_lat', e.target.value)} /></Campo>
        <Campo label="Origen lng"><input style={EDIT_INPUT} type="number" step="any" value={form.origen_lng} onChange={e => setF('origen_lng', e.target.value)} /></Campo>
        <Campo label="Km/L (override)"><input style={EDIT_INPUT} type="number" step="any" value={form.km_por_litro_override} onChange={e => setF('km_por_litro_override', e.target.value)} placeholder="default 10" /></Campo>
        <Campo label="Precio litro (override)"><input style={EDIT_INPUT} type="number" step="any" value={form.precio_litro_override} onChange={e => setF('precio_litro_override', e.target.value)} placeholder="default 88.67" /></Campo>
        <Campo label="Tarifa por viaje ($)"><input style={EDIT_INPUT} type="number" step="any" value={form.tarifa_facturada_por_viaje} onChange={e => setF('tarifa_facturada_por_viaje', e.target.value)} /></Campo>
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={EDIT_LABEL}>Notas</label>
        <textarea style={{ ...EDIT_INPUT, minHeight: 60, resize: 'vertical' }} value={form.notas} onChange={e => setF('notas', e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 14, color: '#1a3461', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!form.activo} onChange={e => setF('activo', e.target.checked)} />
        Activo
      </label>
      {msg && <div className="alert-error" style={{ marginTop: 12 }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={onGuardar} disabled={guardando}
          style={{ padding: '9px 18px', background: '#1a3461', color: '#fff', border: 'none', borderRadius: 8, cursor: guardando ? 'default' : 'pointer', fontWeight: 700, opacity: guardando ? .6 : 1 }}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        <button onClick={onCancelar} disabled={guardando}
          style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
          Cancelar
        </button>
      </div>
    </div>
  )
}
